// Piezas de Promociones en los lugares del wizard; sin la URL de Promociones no se monta ninguna.
import { useContext, useMemo, useSyncExternalStore } from 'react';
import { QueryClient, QueryClientContext } from '@tanstack/react-query';
import { isPromotionsConfigured } from '@/domains/retail/admin/promotions/api/promotions.client';
import type { AfterReferral } from '@/domains/sales/wizard/api/useReferralFlow';
import type { BuildSaleSlots } from '@/domains/sales/wizard/steps/BuildSaleStep';
import type { PaymentMethodSlots } from '@/domains/sales/wizard/steps/PaymentMethodStep';
import type { PhoneSelectSlots } from '@/domains/sales/wizard/steps/PhoneSelectStep';
import type { QuoteSlots } from '@/domains/sales/wizard/steps/QuoteStep';
import type { ReferralsSlots } from '@/domains/sales/wizard/steps/ReferralsStep';
import type { LineSlots } from '@/domains/sales/wizard/components/LineLabels';
import type { ScoreSlots } from '@/domains/sales/wizard/steps/ScoreStep';
import type { SummarySlots } from '@/domains/sales/wizard/steps/SummaryStep';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import { anchorLineOf, extraLinesOf } from '@/domains/sales/wizard/buildSale';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { cachedEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import { AccessoryPromotionsSection } from '../components/AccessoryPromotionsSection';
import { AfterSaleBenefits } from '../components/AfterSaleBenefits';
import { AccessoryDiscountSync } from '../components/AccessoryDiscountSync';
import { GiftsSection } from '../components/GiftsSection';
import { KitPromotionsButton } from '../components/KitPromotionsButton';
import { PhonePromotionsReadOnly } from '../components/PhonePromotionsReadOnly';
import { PromotionsSync } from '../components/PromotionsSync';
import { PromotionNameLabel } from '../components/PromotionNameLabel';
import { ReferralPromotionsBanner } from '../components/ReferralPromotionsBanner';
import { ReferredByNotice } from '../components/ReferredByNotice';
import { SaleClosingBenefits } from '../components/SaleClosingBenefits';
import { PaymentOptionPromotions } from '../components/PaymentOptionPromotions';
import { PaymentTrackTotal } from '../components/PaymentTrackTotal';
import { PhoneDiscountCodeField } from '../components/PhoneDiscountCodeField';
import { SkuPromotionsDetail } from '../components/SkuPromotionsDetail';
import { TeamPromotionSelector } from '../components/TeamPromotionSelector';
import { SummaryGiftName } from '../components/SummaryGiftName';
import { createPromotionsAfterReferral } from '../hooks/promotionsAfterReferral';
import { createRemoveGift } from '../hooks/removeGift';
import { createPhonePromotionsCommit, phoneContextOf } from '../hooks/phonePromotionsCommit';
import { seedFromSession } from '../lib/applySelection';
import { heldSignature, phonePricing, type PhoneDraft } from '../lib/phoneDraft';
import {
    accessoryPromotionsOf,
    cardPromotionsOf,
    isAccessoryOnly,
    storePromotionGroupsOf,
} from '../lib/phoneSelectPromotions';
import { chosenPromotionIds } from '../lib/promotionSelection';
import { sessionPromotionState, useSessionPromotionState } from '../store/promotionSelectionStore';
import {
    createPromotionsOnSaleChanged,
    seedFromCachedSession,
} from '../hooks/promotionsOnSaleChanged';
import { extraSaleBenefits, NO_EXTRAS_SALE, type ExtrasSaleContext } from '../lib/extrasBenefits';
import {
    forAnchor,
    isSureKeepOnly,
    saleWithoutSureKeep,
    sureKeepOnlyDiscount,
} from '../lib/promotionAppliesTo';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import type { PaymentSlots } from '@/domains/sales/wizard/steps/PaymentStep';
import { paymentNotEligibleCopy } from '../lib/promotionErrorCopy';
import { cardSaleBenefits } from '../lib/saleBenefits';

export interface PromotionWizardSlots {
    phoneSelect?: PhoneSelectSlots;
    buildSale?: BuildSaleSlots;
    paymentMethod?: PaymentMethodSlots;
    quote?: QuoteSlots;
    /** Cobro: enganche de crédito o pago de contado. */
    payment?: PaymentSlots;
    score?: ScoreSlots;
    summary?: SummarySlots;
    referrals?: ReferralsSlots;
}

export interface PromotionWizardSlotsDeps {
    sessionId: string;
    queryClient?: QueryClient;
    /** Promos de la tienda ya leídas (las pide el banner); de ellas salen burbujas y precios. */
    storePromotions?: readonly StoreSalePromotionItem[];
    /** Promo elegida y extras de la venta: de ellos sale el precio de cada extra. */
    extrasSale?: ExtrasSaleContext;
    /** Lo marcado en «Elige el teléfono»; con él cambia el desglose del precio. */
    phoneDraft?: PhoneDraft;
}

export function buildPromotionWizardSlots({
    sessionId,
    queryClient,
    storePromotions: loadedStorePromotions,
    extrasSale = NO_EXTRAS_SALE,
    phoneDraft,
}: PromotionWizardSlotsDeps): PromotionWizardSlots {
    const storePromotions = loadedStorePromotions ?? [];
    const referral = createPromotionsAfterReferral({ queryClient });
    const afterReferral: AfterReferral = (ctx) => {
        seedFromCachedSession(sessionId, queryClient);
        return referral(ctx);
    };
    const onSaleChanged = createPromotionsOnSaleChanged({ sessionId, queryClient });
    const giftName: NonNullable<LineSlots['giftName']> = (gift) => (
        <SummaryGiftName sessionId={sessionId} sku={gift.sku} catalogName={gift.name} />
    );
    const promotionLabel: NonNullable<LineSlots['promotionLabel']> = (ctx) => (
        <PromotionNameLabel sessionId={sessionId} {...ctx} />
    );
    const lineSlots: LineSlots = { giftName, promotionLabel };
    return {
        phoneSelect: {
            referredByNotice: (customerName) => (
                <ReferredByNotice sessionId={sessionId} customerName={customerName} />
            ),
            saleBenefits: (item) => cardSaleBenefits(item, storePromotions),
            storePromotionGroups: storePromotionGroupsOf(storePromotions),
            cardPromotions: (item) => cardPromotionsOf(item, storePromotions),
            accessoryPromotions: (item) => accessoryPromotionsOf(item, storePromotions),
            promotionPicker: (item, ctx) => (
                <TeamPromotionSelector
                    sessionId={sessionId}
                    item={item}
                    {...(ctx.heldLineId !== undefined && { heldLineId: ctx.heldLineId })}
                    withoutSureKeep={!sureKeepFinances(item)}
                    readOnly={ctx.readOnly}
                />
            ),
            // Lo que el selector no lista (bonos, beneficios, bloqueadas), en solo lectura.
            detailPromotions: (item, _ctx, renderBonuses) => {
                const draft = phoneDraft ?? sessionPromotionState(sessionId).phoneDraft;
                if (draft?.sku !== item.sku) return null;
                return (
                    <SkuPromotionsDetail
                        sessionId={sessionId}
                        sku={item.sku}
                        renderBonuses={renderBonuses}
                        storePromotions={loadedStorePromotions}
                        withoutSureKeep={!sureKeepFinances(item)}
                        omitPromotionIds={[
                            ...draft.options.map((o) => o.promotionId),
                            ...storePromotions.filter(isAccessoryOnly).map((p) => p.promotionId),
                        ]}
                    />
                );
            },
            discountCode: ({ sku, disabled }) => (
                <PhoneDiscountCodeField sessionId={sessionId} sku={sku} disabled={disabled} />
            ),
            pricing: (item, heldLine) =>
                phonePricing(
                    item,
                    phoneDraft ?? sessionPromotionState(sessionId).phoneDraft,
                    heldLine && {
                        line: heldLine,
                        signature: heldLineSignature(sessionId, heldLine, queryClient),
                    },
                    (id) => rememberedPromotionName(sessionId, id),
                    (id) => rememberedSureKeepOnly(sessionId, id),
                ),
            commitPromotions: createPhonePromotionsCommit({ sessionId, queryClient }),
        },
        buildSale: {
            promotionsSync: (ctx) => <PromotionsSync sessionId={sessionId} {...ctx} />,
            gifts: (ctx) => <GiftsSection sessionId={sessionId} {...ctx} />,
            phonePromotions: (ctx) => <PhonePromotionsReadOnly sessionId={sessionId} {...ctx} />,
            kitPromotions: (ctx) => <KitPromotionsButton sessionId={sessionId} {...ctx} />,
            accessoryDiscount: (ctx) => (
                <AccessoryDiscountSync sessionId={sessionId} context={ctx} />
            ),
            removeGift: createRemoveGift({ sessionId, queryClient }),
            accessoryPromotions: (ctx) => (
                <AccessoryPromotionsSection sessionId={sessionId} context={ctx} />
            ),
            // El mismo precio con promo del kit que en «Elige el teléfono».
            kitBenefits: (item) => cardSaleBenefits(item, storePromotions),
            extraBenefits: (item) => {
                const benefits = extraSaleBenefits(
                    item,
                    forAnchor(storePromotions, anchorWithoutSureKeep(sessionId, queryClient)),
                    extrasSale,
                );
                return benefits;
            },
            sureKeepOnlyDiscountCents: (session) =>
                sureKeepOnlyDiscount(session.reservedLines ?? [], {
                    promotion: (id) => evaluatedSureKeepOnly(sessionId, id, queryClient),
                }),
            afterSale: <AfterSaleBenefits sessionId={sessionId} />,
            giftName,
            promotionLabel,
            onSaleChanged,
        },
        paymentMethod: {
            onSaleChanged,
            afterReferral,
            // Cada tarjeta muestra solo lo propio: SureKeep lo que gana, Contado lo que pierde.
            paymentOption: (ctx) =>
                ctx.track === 'EXTERNAL' ? null : (
                    <PaymentOptionPromotions
                        sessionId={sessionId}
                        {...ctx}
                        alwaysOpen
                        hideTotal
                        showOnly={ctx.track === 'SUREKEEP' ? 'sureKeepGains' : 'sureKeepLosses'}
                    />
                ),
            trackTotal: (ctx) => <PaymentTrackTotal sessionId={sessionId} {...ctx} />,
            giftName,
            promotionLabel,
        },
        quote: lineSlots,
        payment: { ...lineSlots, promotionChargeCopy: paymentNotEligibleCopy },
        score: { afterReferral, onSaleChanged },
        summary: { coupon: (ctx) => <SaleClosingBenefits {...ctx} />, lines: lineSlots },
        referrals: {
            promotionsBanner: (ctx) => <ReferralPromotionsBanner sessionId={sessionId} {...ctx} />,
        },
    };
}

/** La promo solo aplica con SureKeep, según la evaluación de la venta (o lo visto en la sesión). */
function evaluatedSureKeepOnly(
    sessionId: string,
    promotionId: string,
    queryClient?: QueryClient,
): boolean {
    const evaluation = cachedEvaluation(sessionId, queryClient);
    const promo = evaluation?.promotions.find((p) => p.promotionId === promotionId);
    return promo ? isSureKeepOnly(promo) : rememberedSureKeepOnly(sessionId, promotionId);
}

/** Lo apartado en la línea del teléfono, en la forma del selector, para saber si cambió. */
function heldLineSignature(
    sessionId: string,
    line: ReservedLine,
    queryClient?: QueryClient,
): string {
    const state = sessionPromotionState(sessionId);
    const draft = state.phoneDraft ?? { options: [] };
    if (state.synced && state.selection.anchorLineId === line.lineId) {
        return heldSignature(state.selection, line.lineId, draft);
    }
    const session = queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
    const selection = session
        ? seedFromSession(phoneContextOf(session, line))
        : { anchorLineId: line.lineId, byLine: {}, code: line.discountCode ?? null };
    return heldSignature(selection, line.lineId, draft);
}

/** Sin SureKeep para la venta: no financia el equipo o el cliente no califica (según la sesión en caché). */
function anchorWithoutSureKeep(sessionId: string, queryClient?: QueryClient): boolean {
    const session = queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
    return session ? saleWithoutSureKeep(session) : false;
}

const noSubscription = (): (() => void) => () => undefined;

/** Sin proveedor de React Query, un cliente propio para que el hook no truene (no pide nada). */
const fallbackClient = new QueryClient({ defaultOptions: { queries: { enabled: false } } });

/** Lugares del wizard con las piezas de Promociones; vacío si Promociones no está configurado. */
export function usePromotionWizardSlots(sessionId: string): PromotionWizardSlots {
    const queryClient = useContext(QueryClientContext);
    // Las pide el slot mismo: el precio de los extras no depende de que el banner las haya leído.
    const storePromotions = useStoreSalePromotions(sessionId, queryClient ?? fallbackClient).data
        ?.items;
    const session = useCachedSession(queryClient, sessionId);
    const promotionState = useSessionPromotionState(sessionId);
    const chosenIds = chosenPromotionIds(promotionState.selection).join('|');
    const { phoneDraft } = promotionState;
    const anchor = session ? anchorLineOf(session) : undefined;
    const extrasSale = useMemo<ExtrasSaleContext>(
        () => ({
            // La que baja los accesorios entre las elegidas.
            chosen:
                storePromotions?.find(
                    (p) =>
                        p.levers.accessoryDiscount && chosenIds.split('|').includes(p.promotionId),
                ) ?? null,
            extraLines: session ? extraLinesOf(session) : [],
            ...(anchor && { anchorLine: anchor }),
        }),
        [storePromotions, chosenIds, session, anchor],
    );
    const configured = isPromotionsConfigured();
    return useMemo(
        () =>
            configured
                ? buildPromotionWizardSlots({
                      sessionId,
                      queryClient,
                      extrasSale,
                      ...(storePromotions && { storePromotions }),
                      ...(phoneDraft && { phoneDraft }),
                  })
                : {},
        [configured, sessionId, queryClient, storePromotions, extrasSale, phoneDraft],
    );
}

/** La sesión del wizard en caché, sin pedirla. */
function useCachedSession(
    queryClient: QueryClient | undefined,
    sessionId: string,
): WizardSession | undefined {
    const subscribe = useMemo(
        () =>
            queryClient
                ? (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange)
                : noSubscription,
        [queryClient],
    );
    return useSyncExternalStore(subscribe, () =>
        queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)),
    );
}
